import React, { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { BellRing, Mail, MessageCircle, Smartphone, MonitorSmartphone, Send, Loader2, Info } from 'lucide-react'

// NotificationsSettings — the /notifications settings page. Channel toggles
// (in-app / email / WhatsApp / SMS), a digest cadence (off / daily / weekly),
// and email + phone fields. "Send me a preview" POSTs /digest/send. Prefs are
// loaded from GET /notifications/prefs and saved with PUT /notifications/prefs.
// Email/SMS delivery needs provider keys (SendGrid / Twilio); when a channel
// is unavailable we show a "connect to enable" hint and disable its toggle.
//
// VISUAL LAYER ONLY: flat "Nunito" aesthetic scoped under `.ns-root`
// (styles prefixed `.ns-*`), light+dark theme tokens — mirrors Connections.

const API = '/api/v1'

const NS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');
.ns-root{
  --ns-bg:#FBFBF7; --ns-panel:#FFFFFF; --ns-panel2:#F6F6F0; --ns-line:#ECEBE3;
  --ns-ink:#2E3A45; --ns-mut:#7C8794; --ns-dim:#AEB6BD; --ns-accent:#4E9AC4;
  --ns-accent-soft:#EAF4FA; --ns-warn:#E0A93B; --ns-warn-soft:#FBF3E1;
  --ns-shadow:0 1px 2px rgba(46,58,69,.05),0 10px 26px rgba(46,58,69,.07);
  font-family:Nunito,system-ui,-apple-system,Segoe UI,sans-serif;
  color:var(--ns-ink); -webkit-font-smoothing:antialiased;
}
[data-theme="dark"] .ns-root{
  --ns-bg:#0E1116; --ns-panel:#171B22; --ns-panel2:#12161C; --ns-line:#262C36;
  --ns-ink:#E7ECF1; --ns-mut:#95A0AD; --ns-dim:#5C6672; --ns-accent:#5FB0DB;
  --ns-accent-soft:#132029; --ns-warn:#E8BB5A; --ns-warn-soft:#2A2415;
  --ns-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
}
@media (prefers-color-scheme:dark){:root:not([data-theme="light"]) .ns-root{
  --ns-bg:#0E1116; --ns-panel:#171B22; --ns-panel2:#12161C; --ns-line:#262C36;
  --ns-ink:#E7ECF1; --ns-mut:#95A0AD; --ns-dim:#5C6672; --ns-accent:#5FB0DB;
  --ns-accent-soft:#132029; --ns-warn:#E8BB5A; --ns-warn-soft:#2A2415;
  --ns-shadow:0 1px 2px rgba(0,0,0,.3),0 10px 26px rgba(0,0,0,.4);
}}
.ns-root *{box-sizing:border-box;}
.ns-title{font-size:24px;font-weight:800;letter-spacing:-.02em;margin:0;display:flex;align-items:center;gap:10px;color:var(--ns-ink);}
.ns-sub{margin:4px 0 0;color:var(--ns-mut);font-size:14px;font-weight:500;line-height:1.5;}
.ns-card{background:var(--ns-panel);border:1px solid var(--ns-line);border-radius:16px;box-shadow:var(--ns-shadow);padding:22px 22px;}
.ns-card + .ns-card{margin-top:18px;}
.ns-h{font-size:13px;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:var(--ns-mut);margin:0 0 4px;}
.ns-hint{font-size:12.5px;font-weight:500;color:var(--ns-dim);margin:0 0 16px;line-height:1.5;}
.ns-chan{display:flex;align-items:center;gap:13px;padding:13px 0;border-bottom:1px solid var(--ns-line);}
.ns-chan:last-child{border-bottom:none;}
.ns-ico{flex:0 0 auto;width:38px;height:38px;border-radius:11px;display:flex;align-items:center;justify-content:center;background:var(--ns-accent-soft);color:var(--ns-accent);}
.ns-cbody{flex:1;min-width:0;}
.ns-cname{font-size:14px;font-weight:800;color:var(--ns-ink);display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
.ns-cdesc{font-size:12.5px;font-weight:500;color:var(--ns-mut);margin-top:2px;}
.ns-tag{font-size:10px;font-weight:800;text-transform:uppercase;letter-spacing:.05em;padding:2px 7px;border-radius:6px;background:var(--ns-warn-soft);color:var(--ns-warn);border:1px solid var(--ns-warn);}
.ns-sw{flex:0 0 auto;position:relative;width:44px;height:25px;border-radius:20px;background:var(--ns-line);border:none;cursor:pointer;transition:.18s;padding:0;}
.ns-sw::after{content:"";position:absolute;top:3px;left:3px;width:19px;height:19px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.25);transition:.18s;}
.ns-sw.on{background:var(--ns-accent);}
.ns-sw.on::after{transform:translateX(19px);}
.ns-sw:disabled{opacity:.5;cursor:not-allowed;}
.ns-seg{display:inline-flex;gap:4px;padding:5px;border-radius:12px;background:var(--ns-panel2);border:1px solid var(--ns-line);}
.ns-seg button{font-family:inherit;font-size:13px;font-weight:800;border-radius:9px;padding:8px 16px;cursor:pointer;border:1px solid transparent;background:none;color:var(--ns-mut);transition:.16s;}
.ns-seg button:hover{color:var(--ns-ink);}
.ns-seg button.active{background:var(--ns-panel);color:var(--ns-ink);border-color:var(--ns-line);box-shadow:var(--ns-shadow);}
.ns-field{margin-top:14px;}
.ns-label{display:block;font-size:12px;font-weight:800;color:var(--ns-mut);margin-bottom:6px;}
.ns-input{width:100%;font-family:inherit;font-size:14px;font-weight:600;color:var(--ns-ink);background:var(--ns-bg);border:1px solid var(--ns-line);border-radius:11px;padding:11px 13px;outline:none;transition:.16s;}
.ns-input:focus{border-color:var(--ns-accent);box-shadow:0 0 0 3px var(--ns-accent-soft);}
.ns-input::placeholder{color:var(--ns-dim);}
.ns-row{display:flex;gap:14px;flex-wrap:wrap;}
.ns-row > .ns-field{flex:1;min-width:200px;margin-top:14px;}
.ns-actions{display:flex;align-items:center;gap:10px;margin-top:22px;flex-wrap:wrap;}
.ns-btn{font-family:inherit;font-size:13.5px;font-weight:800;border-radius:11px;padding:11px 18px;cursor:pointer;display:inline-flex;align-items:center;gap:8px;border:1px solid transparent;transition:.16s;}
.ns-btn.primary{background:var(--ns-accent);color:#fff;}
.ns-btn.primary:hover{filter:brightness(1.05);}
.ns-btn.ghost{background:var(--ns-panel);color:var(--ns-ink);border-color:var(--ns-line);}
.ns-btn.ghost:hover{background:var(--ns-panel2);}
.ns-btn:disabled{opacity:.55;cursor:not-allowed;filter:none;}
.ns-note{display:flex;gap:9px;align-items:flex-start;margin-top:16px;padding:11px 13px;border-radius:11px;background:var(--ns-warn-soft);border:1px solid var(--ns-warn);color:var(--ns-ink);font-size:12.5px;font-weight:600;line-height:1.5;}
.ns-note svg{flex:0 0 auto;color:var(--ns-warn);margin-top:1px;}
.ns-spin{animation:ns-spin 1s linear infinite;}
@keyframes ns-spin{to{transform:rotate(360deg);}}
.ns-skel{height:14px;border-radius:6px;background:var(--ns-panel2);animation:ns-pulse 1.2s ease-in-out infinite;}
@keyframes ns-pulse{0%,100%{opacity:.5;}50%{opacity:1;}}
`

let _nsStyleInjected = false
function NSStyle() {
  useEffect(() => {
    if (_nsStyleInjected) return
    const el = document.createElement('style')
    el.setAttribute('data-ns-style', '1')
    el.textContent = NS_CSS
    document.head.appendChild(el)
    _nsStyleInjected = true
  }, [])
  return null
}

const CHANNELS = [
  { key: 'inApp',    label: 'In-app',   icon: MonitorSmartphone, desc: 'The bell in your top bar — always on.', always: true },
  { key: 'email',    label: 'Email',    icon: Mail,              desc: 'Digests and alerts to your inbox.',       provider: 'SendGrid' },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle,     desc: 'Messages to your WhatsApp number.',        provider: 'WhatsApp Cloud API' },
  { key: 'sms',      label: 'SMS',      icon: Smartphone,        desc: 'Text messages to your phone.',             provider: 'Twilio' },
]

const CADENCES = [
  { key: 'off',    label: 'Off' },
  { key: 'daily',  label: 'Daily' },
  { key: 'weekly', label: 'Weekly' },
]

const DEFAULT_PREFS = {
  channels: { inApp: true, email: false, whatsapp: false, sms: false },
  cadence: 'weekly',
  email: '',
  phone: '',
}

export default function NotificationsSettings() {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS)
  const [available, setAvailable] = useState({ inApp: true, email: false, whatsapp: true, sms: false })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [previewing, setPreviewing] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await axios.get(`${API}/notifications/prefs`)
      const d = r.data || {}
      setPrefs({
        channels: { ...DEFAULT_PREFS.channels, ...(d.channels || {}) },
        cadence: d.cadence || DEFAULT_PREFS.cadence,
        email: d.email || '',
        phone: d.phone || '',
      })
      // Availability may arrive as `available`, or as per-provider flags.
      if (d.available && typeof d.available === 'object') {
        setAvailable(a => ({ ...a, ...d.available }))
      }
    } catch {
      // No prefs yet / not provisioned — fall back to defaults, stay silent.
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const isAvailable = (ch) => ch.always || available[ch.key] !== false
  const wa = (ch) => (ch.always ? '' : available[ch.key] === false ? ` — connect ${ch.provider} to enable` : '')

  const toggleChannel = (key) => {
    setPrefs(p => ({ ...p, channels: { ...p.channels, [key]: !p.channels[key] } }))
  }

  const save = async () => {
    setSaving(true)
    try {
      await axios.put(`${API}/notifications/prefs`, prefs)
      toast.success('Notification preferences saved')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not save preferences')
    } finally {
      setSaving(false)
    }
  }

  const sendPreview = async () => {
    setPreviewing(true)
    try {
      await axios.post(`${API}/notifications/digest/send`, {
        preview: true,
        cadence: prefs.cadence,
        email: prefs.email,
        phone: prefs.phone,
      })
      toast.success('Preview sent — check your enabled channels')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send preview')
    } finally {
      setPreviewing(false)
    }
  }

  const anyUnavailable = CHANNELS.some(ch => !ch.always && !isAvailable(ch))

  return (
    <div className="ns-root" style={{ maxWidth: 720 }}>
      <NSStyle />
      <div style={{ marginBottom: 20 }}>
        <h2 className="ns-title"><BellRing size={24} style={{ color: 'var(--ns-accent)' }} /> Notifications</h2>
        <p className="ns-sub">Choose where alerts reach you and how often we send a digest.</p>
      </div>

      {loading ? (
        <div className="ns-card">
          <div className="ns-skel" style={{ width: '40%', marginBottom: 16 }} />
          <div className="ns-skel" style={{ width: '90%', marginBottom: 12 }} />
          <div className="ns-skel" style={{ width: '75%', marginBottom: 12 }} />
          <div className="ns-skel" style={{ width: '85%' }} />
        </div>
      ) : (
        <>
          <div className="ns-card">
            <p className="ns-h">Channels</p>
            <p className="ns-hint">Turn on the places you want to be notified.</p>
            {CHANNELS.map(ch => {
              const Icon = ch.icon
              const enabled = isAvailable(ch)
              const on = !!prefs.channels[ch.key] && enabled
              return (
                <div className="ns-chan" key={ch.key}>
                  <div className="ns-ico"><Icon size={19} /></div>
                  <div className="ns-cbody">
                    <div className="ns-cname">
                      {ch.label}
                      {!enabled && <span className="ns-tag">Connect to enable</span>}
                    </div>
                    <div className="ns-cdesc">{ch.desc}{wa(ch)}</div>
                  </div>
                  <button
                    className={`ns-sw${on ? ' on' : ''}`}
                    onClick={() => enabled && toggleChannel(ch.key)}
                    disabled={!enabled}
                    role="switch"
                    aria-checked={on}
                    aria-label={`${ch.label} notifications`}
                  />
                </div>
              )
            })}
            {anyUnavailable && (
              <div className="ns-note">
                <Info size={16} />
                <span>Email and SMS delivery need provider keys (SendGrid / Twilio). Once connected in Connections, those toggles switch on here.</span>
              </div>
            )}
          </div>

          <div className="ns-card">
            <p className="ns-h">Digest cadence</p>
            <p className="ns-hint">How often to roll up your notifications into one summary.</p>
            <div className="ns-seg" role="tablist">
              {CADENCES.map(c => (
                <button
                  key={c.key}
                  role="tab"
                  aria-selected={prefs.cadence === c.key}
                  className={prefs.cadence === c.key ? 'active' : ''}
                  onClick={() => setPrefs(p => ({ ...p, cadence: c.key }))}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="ns-card">
            <p className="ns-h">Delivery details</p>
            <p className="ns-hint">Where email and SMS/WhatsApp messages should go.</p>
            <div className="ns-row">
              <div className="ns-field">
                <label className="ns-label" htmlFor="ns-email">Email address</label>
                <input
                  id="ns-email"
                  className="ns-input"
                  type="email"
                  placeholder="you@company.com"
                  value={prefs.email}
                  onChange={e => setPrefs(p => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="ns-field">
                <label className="ns-label" htmlFor="ns-phone">Phone number</label>
                <input
                  id="ns-phone"
                  className="ns-input"
                  type="tel"
                  placeholder="+1 555 000 1234"
                  value={prefs.phone}
                  onChange={e => setPrefs(p => ({ ...p, phone: e.target.value }))}
                />
              </div>
            </div>

            <div className="ns-actions">
              <button className="ns-btn primary" onClick={save} disabled={saving}>
                {saving ? <Loader2 size={15} className="ns-spin" /> : null}
                {saving ? 'Saving…' : 'Save preferences'}
              </button>
              <button className="ns-btn ghost" onClick={sendPreview} disabled={previewing}>
                {previewing ? <Loader2 size={15} className="ns-spin" /> : <Send size={15} />}
                {previewing ? 'Sending…' : 'Send me a preview'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
